import { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import cx from 'clsx';
import { Avatar, avatarType, fontWeight, Heading, headingLevel, Text } from '../../../../../packages';
import { OPENTOK_APIKEY } from '../../../../../constants/CommonConstants';
import { MemberDisconnected } from '../../../../../packages/ui/templates/sessions/section-detail/components/member-disconnected';
import { sendSessionJoinedSegment } from '../../../../../services/appointment/appointment.service';
import { TWILIO_CALL_STATE } from '../../../../../packages/ui/templates/twilio-call/twilio.constants';
import TwilioCall from '../../../../../packages/ui/templates/twilio-call/TwilioCall';
import TopbarView from './topbar-view';
import BottomView from './bottom-view';
import { useStyles } from '../VideoCallScreen.styles';
import SocketClient from '../../../../../utils/SocketClient';
import { Sections } from '../../../../../packages/ui/templates/sessions/sessions.constants';
import { btnSize, btnType, Button } from '../../../../../packages/ui/atoms/button';
import { Input, inputSize, inputType } from '../../../../../packages/ui/atoms/input';
import { colors } from '../../../../../packages/colors';

/**
 * StreamContainer Component
 * Handles all video streaming logic for both waiting and active session states
 * - Waiting: Shows provider video + timer
 * - Active: Shows provider + subscriber videos + controls
 */
const StreamContainer = ({
  appointment,
  telesessionId,
  telesessionToken,
  isInPersonSessionCall,
  isWaiting,
  setIsWaiting,
  setShowCompletedByPhone,
  duration,
  handleMemberActionClick,
  onSessionEndSignalRef,
  onMemberEndedSession,
  onGracePeriodExpired,
  onTwilioCallStateChange,
  onMemberJoinedWithActiveCall,
  scratchpadData,
  currentSection,
}) => {
  const classes = useStyles();

  // Track Twilio call state
  const [twilioCallState, setTwilioCallState] = useState(null);

  // OpenTok refs
  const sessionRef = useRef(null);
  const publisherRef = useRef(null);
  const screenPublisherRef = useRef(null);
  const subscriberRef = useRef(null);
  const publisherContainerRef = useRef(null);
  const screenContainerRef = useRef(null);
  const subscriberContainerRef = useRef(null);

  // Video states
  const [publishVideo, setPublishVideo] = useState(true);
  const [publishAudio, setPublishAudio] = useState(true);
  const [showShareScreenSelection, setShowShareScreenSelection] = useState(false);
  const [memberVideoEnable, setMemberVideoEnable] = useState(true);
  const [subscriberJoined, setSubscriberJoined] = useState(false);
  const [participantScreenShared, setParticipantScreenShared] = useState(false);
  const [mediaPermissionsGranted, setMediaPermissionsGranted] = useState(false);
  const [memberMediaPermissionsGranted, setMemberMediaPermissionsGranted] = useState(true); // Assume true initially
  const [segmentEventSent, setSegmentEventSent] = useState(false);

  const isSessionEndedRef = useRef(false);
  const twilioHangupRef = useRef(null); // Store Twilio hangup handler
  const twilioCallStateRef = useRef(null); // Store current call state for event handlers

  // Scratchpad modal state
  const [scratchpadModalOpen, setScratchpadModalOpen] = useState(false);
  const [scratchpadInput, setScratchpadInput] = useState('');

  // Update scratchpad input when scratchpadData changes
  useEffect(() => {
    if (scratchpadData?.scratchpad) {
      setScratchpadInput(scratchpadData.scratchpad);
    }
  }, [scratchpadData?.scratchpad]);

  const handleScratchpadClick = () => {
    setScratchpadModalOpen(true);
  };

  const handleScratchpadClose = () => {
    setScratchpadModalOpen(false);
    // Reset to current saved value
    if (scratchpadData?.scratchpad) {
      setScratchpadInput(scratchpadData.scratchpad);
    }
  };

  const handleScratchpadSave = () => {
    if (scratchpadData?.addScratchpadNotes) {
      scratchpadData.addScratchpadNotes(scratchpadInput);
    }
    // setScratchpadModalOpen(false);
  };

  // Determine if scratchpad button should be shown (only on Sections.EVALUATIONS)
  const showScratchpadButton = !isWaiting && currentSection === Sections.EVALUATIONS;

  /**
   * Handle Twilio call state changes from MemberDisconnected component
   */
  const handleTwilioCallStateChange = state => {
    setTwilioCallState(state);
    twilioCallStateRef.current = state; // Also update ref for event handlers

    // Notify parent component about state change
    if (onTwilioCallStateChange) {
      onTwilioCallStateChange(state);
    }
  };

  /**
   * Send session-ended signal to member and disconnect
   * This is exposed via ref so parent components can trigger it
   */
  const sendSessionEndedSignal = () => {
    // Disconnect Twilio call if active
    if (twilioHangupRef.current && twilioCallStateRef.current === TWILIO_CALL_STATE.CONNECTED) {
      twilioHangupRef.current();
    }

    if (sessionRef.current) {
      // Send signal to other participants (member)
      sessionRef.current.signal(
        {
          type: 'session-ended',
          data: 'The session has ended',
        },
        error => {
          if (error) {
            console.log('Error sending session-ended signal:', error);
          } else {
            console.log('Session-ended signal sent successfully');
          }
        },
      );

      // Disconnect from the session
      sessionRef.current.disconnect();
    }
  };

  /**
   * Expose sendSessionEndedSignal via ref for parent to call
   */
  useEffect(() => {
    if (onSessionEndSignalRef) {
      onSessionEndSignalRef.current = sendSessionEndedSignal;
    }
  }, [onSessionEndSignalRef]);

  /**
   * Cleanup: Disconnect Twilio call when component unmounts (user navigates away)
   */
  useEffect(() => {
    return () => {
      if (twilioHangupRef.current && twilioCallStateRef.current === TWILIO_CALL_STATE.CONNECTED) {
        twilioHangupRef.current();
      }
    };
  }, []);

  /**
   * Check and request media permissions (camera and microphone)
   * This will trigger the browser's permission popup
   */
  const checkMediaPermissions = async () => {
    try {
      if (isInPersonSessionCall) {
        // Request microphone only for in-person sessions
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          stream.getTracks().forEach(t => t.stop());
          return true;
        } catch (err) {
          console.error('Microphone access denied:', err);
          return false;
        }
      }

      // Request camera and microphone for video sessions
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
        stream.getTracks().forEach(t => t.stop());
        return true;
      } catch (err) {
        console.error('Camera/Microphone access denied:', err);
        return false;
      }
    } catch (err) {
      console.error('Error requesting media permissions:', err);
      return false;
    }
  };

  // Initial permission check
  useEffect(() => {
    void (async () => {
      const granted = await checkMediaPermissions();
      setMediaPermissionsGranted(granted);
    })();
  }, []);

  // Listen for member's media permission status via socket
  useEffect(() => {
    if (!appointment?.appointmentId) return;

    try {
      const socketClient = SocketClient.getInstance();
      const socket = socketClient.getConnectedSocket();

      if (!socket) {
        console.warn('Socket not connected, cannot listen for member permission status');
        return;
      }
      const handleMemberPermissionStatus = data => {
        // Only update if this event is for the current appointment
        if (data?.appointmentId === appointment.appointmentId) {
          setMemberMediaPermissionsGranted(data.mediaPermissionsGranted);
        }
      };
      socket.on('member-media-permission-status', handleMemberPermissionStatus);
      // Cleanup
      return () => {
        if (socket) {
          socket.off('member-media-permission-status', handleMemberPermissionStatus);
          socket.offAny();
        }
      };
    } catch (err) {
      console.warn('Failed to set up socket listener for member permission status:', err);
    }
  }, [appointment?.appointmentId]);

  // Initialize OpenTok session
  useEffect(() => {
    if (telesessionId && telesessionToken && !isSessionEndedRef.current && mediaPermissionsGranted) {
      sessionRef.current = OT.initSession(OPENTOK_APIKEY, telesessionId);

      // Set up session event handlers
      sessionRef.current.on('streamCreated', async event => {
        await checkMediaPermissions();

        // Check if this is a screen share stream
        if (event.stream.videoType === 'screen') {
          setParticipantScreenShared(true);

          const screenSubscriberOptions = {
            insertMode: 'append',
            width: '100%',
            height: '100%',
            fitMode: 'contain',
            subscribeToAudio: false,
            subscribeToVideo: true,
            style: { buttonDisplayMode: 'off' },
          };

          sessionRef.current.subscribe(
            event.stream,
            document.getElementById('memberScreenContainer'),
            screenSubscriberOptions,
            error => {
              if (error) {
                console.log('Error subscribing to screen share:', error);
              } else {
                console.log('Successfully subscribed to screen share');
              }
            },
          );
        } else {
          // Regular video stream handling
          if (!subscriberContainerRef.current || !sessionRef.current) return;

          setSubscriberJoined(true);

          const subscriberOptions = {
            insertMode: 'append',
            width: '100%',
            height: '100%',
            fitMode: participantScreenShared ? 'contain' : 'cover',
            subscribeToAudio: true,
            subscribeToVideo: true,
            style: { buttonDisplayMode: 'off' },
          };

          subscriberRef.current = sessionRef.current.subscribe(
            event.stream,
            subscriberContainerRef.current,
            subscriberOptions,
            error => {
              if (error) {
                console.log('There was an error subscribing');
              } else {
                console.log('Subscriber connected to the stream');
                setIsWaiting(false);
                setMemberVideoEnable(true);

                // Disconnect Twilio call if active when member joins
                if (twilioHangupRef.current && twilioCallStateRef.current === TWILIO_CALL_STATE.CONNECTED) {
                  twilioHangupRef.current();
                  // Show toast notification
                  if (onMemberJoinedWithActiveCall) {
                    onMemberJoinedWithActiveCall();
                  }
                }

                // Send segment event when member joins (only once)
                if (!segmentEventSent) {
                  setSegmentEventSent(true);
                  sendSessionJoinedSegment(appointment.appointmentId)
                    .then(() => {
                      console.log('Session joined segment event sent successfully');
                    })
                    .catch(error => {
                      console.error('Error sending session joined segment event:', error);
                    });
                }
              }
            },
          );

          // Set up subscriber event handlers
          subscriberRef.current.on('videoDisabled', () => {
            console.log('Subscriber video disabled');
            setMemberVideoEnable(false);
          });

          subscriberRef.current.on('videoEnabled', () => {
            console.log('Subscriber video enabled');
            setMemberVideoEnable(true);
          });

          subscriberRef.current.on('disconnected', () => {
            console.log('Subscriber disconnected from the stream');
          });
        }
      });

      sessionRef.current.on('signal', event => {
        if (event.type === 'session-ended' || event.type === 'signal:session-ended') {
          isSessionEndedRef.current = true;

          // Disconnect Twilio call if active when member ends session
          if (twilioHangupRef.current && twilioCallStateRef.current === TWILIO_CALL_STATE.CONNECTED) {
            twilioHangupRef.current();
          }

          // Notify parent that member ended the session
          if (onMemberEndedSession) {
            onMemberEndedSession();
          }
        }
      });

      sessionRef.current.on('streamDestroyed', event => {
        console.log('Stream destroyed');
        if (event.stream.videoType === 'screen') {
          console.log('Member screen sharing stopped');
          setParticipantScreenShared(false);
        } else {
          setSubscriberJoined(false);
        }
      });

      sessionRef.current.on('connectionDestroyed', event => {
        console.log('Connection destroyed');
        setSubscriberJoined(false);
      });

      sessionRef.current.on('sessionConnected', async event => {
        const mediaStream = await checkMediaPermissions();
        if (mediaStream) {
          console.log('Session connected');
        }
      });

      sessionRef.current.on('sessionDisconnected', event => {
        console.log('Session disconnected');
      });

      // Connect to the session
      sessionRef.current.connect(telesessionToken, error => {
        if (error) {
          console.log('Error connecting to the session');
        } else {
          console.log('Session connected');

          // Initialize publisher
          const publisherOptions = {
            insertMode: 'append',
            width: 200,
            height: 114,
            publishVideo: !isInPersonSessionCall && publishVideo,
            publishAudio: publishAudio,
            style: { buttonDisplayMode: 'off' },
            audioOnly: isInPersonSessionCall,
            videoSource: isInPersonSessionCall ? false : undefined,
          };

          publisherRef.current = OT.initPublisher(publisherContainerRef.current, publisherOptions, error => {
            if (error) {
              console.log('There was an error initializing the publisher');
            } else {
              if (sessionRef.current && sessionRef.current.connection) {
                sessionRef.current.publish(publisherRef.current, err => {
                  if (err) {
                    console.log('Error publishing:', err.message);
                  } else {
                    console.log('Publisher initialized and publishing');
                  }
                });
              } else {
                console.warn('Skip publish – session not connected');
              }
            }
          });

          // Set up publisher event handlers
          publisherRef.current.on('streamCreated', async event => {
            await checkMediaPermissions();
            console.log('Publisher stream created');
            setShowCompletedByPhone(true);
          });

          publisherRef.current.on('streamDestroyed', event => {
            console.log('Publisher stream destroyed');
            setSubscriberJoined(false);
            if (sessionRef.current) {
              sessionRef.current.disconnect();
            }
          });

          publisherRef.current.on('mediaStopped', event => {
            console.log('Media Stopped');
            setSubscriberJoined(false);
            if (sessionRef.current) {
              sessionRef.current.disconnect();
            }
          });
        }
      });

      // Cleanup function
      return () => {
        if (screenPublisherRef.current) {
          sessionRef.current.unpublish(screenPublisherRef.current);
          screenPublisherRef.current = null;
        }

        if (publisherRef.current) {
          sessionRef.current.unpublish(publisherRef.current);
          publisherRef.current = null;
        }

        if (subscriberRef.current) {
          sessionRef.current.unsubscribe(subscriberRef.current);
          subscriberRef.current = null;
        }

        if (sessionRef.current) {
          sessionRef.current.disconnect();
          sessionRef.current = null;
        }
      };
    }
  }, [telesessionId, telesessionToken, mediaPermissionsGranted]);

  // Handle video publishing state changes
  useEffect(() => {
    if (publisherRef.current) {
      publisherRef.current.publishVideo(!isInPersonSessionCall && publishVideo);
    }
  }, [publishVideo, isInPersonSessionCall]);

  // Handle audio publishing state changes
  useEffect(() => {
    if (publisherRef.current) {
      publisherRef.current.publishAudio(publishAudio);
    }
  }, [publishAudio]);

  // Make sure participant video is visible when they're screen sharing
  useEffect(() => {
    if (participantScreenShared && !memberVideoEnable) {
      setMemberVideoEnable(true);
    }
  }, [participantScreenShared]);

  // Handle screen sharing
  useEffect(() => {
    if (showShareScreenSelection && sessionRef.current) {
      OT.checkScreenSharingCapability(response => {
        if (!response.supported || response.extensionRegistered === false) {
          console.log('Screen sharing not supported');
          return;
        }

        const screenOptions = {
          videoSource: 'screen',
          width: 200,
          height: 114,
          insertMode: 'append',
        };

        screenPublisherRef.current = OT.initPublisher(screenContainerRef.current, screenOptions, error => {
          if (error) {
            console.log(`Error initializing screen publisher: ${error.message}`);
          } else {
            sessionRef.current.publish(screenPublisherRef.current, error => {
              if (error) {
                console.log(`Error publishing screen: ${error.message}`);
              } else {
                console.log('Screen publisher initialized and publishing');
              }
            });
          }
        });

        screenPublisherRef.current.on('streamDestroyed', event => {
          setShowShareScreenSelection(false);
          screenPublisherRef.current = null;
        });

        screenPublisherRef.current.on('mediaStopped', () => {
          console.log('Screen sharing media stopped');
          setShowShareScreenSelection(false);
          if (screenPublisherRef.current) {
            sessionRef.current.unpublish(screenPublisherRef.current);
            screenPublisherRef.current = null;
          }
        });
      });
    } else if (!showShareScreenSelection && screenPublisherRef.current && sessionRef.current) {
      sessionRef.current.unpublish(screenPublisherRef.current);
      screenPublisherRef.current = null;
    }
  }, [showShareScreenSelection]);

  const toggleVideCall = () => {
    setPublishVideo(!publishVideo);
  };

  const toggleMic = () => {
    setPublishAudio(!publishAudio);
  };

  const onShareScreenClick = () => {
    setShowShareScreenSelection(true);
  };

  const onStopSharingClick = () => {
    if (showShareScreenSelection) {
      setShowShareScreenSelection(false);

      if (screenPublisherRef.current && sessionRef.current) {
        sessionRef.current.unpublish(screenPublisherRef.current);
        screenPublisherRef.current = null;
      }
    }
  };

  // Unified render matching original UpdatedSessionExperience structure
  return (
    <TwilioCall
      identity={appointment?.patientFirstName || appointment?.patientName}
      onCallConnected={() => handleTwilioCallStateChange(TWILIO_CALL_STATE.CONNECTED)}
      onCallDisconnected={() => handleTwilioCallStateChange(TWILIO_CALL_STATE.DISCONNECTED)}
      onCallFailed={err => {
        console.error('Call failed:', err);
        handleTwilioCallStateChange(TWILIO_CALL_STATE.IDLE);
      }}
      onCallStateChange={handleTwilioCallStateChange}
      onIncomingCall={conn => console.log('Incoming call:', conn)}
    >
      {({ callHandler, hangup, mute, callState }) => {
        // Store hangup handler in ref for session end cleanup
        twilioHangupRef.current = hangup;

        return (
          <Box flex={isInPersonSessionCall ? 0 : 1} position="relative" height="100%">
            <TopbarView
              hidden={isInPersonSessionCall}
              publisher={<div ref={publisherContainerRef} style={{ width: 200, height: 114 }} />}
              screen={
                showShareScreenSelection && (
                  <div ref={screenContainerRef} style={{ width: 200, height: 114 }} />
                )
              }
              style={{ position: 'relative', zIndex: isWaiting ? 3 : 1 }}
              showPermissionIcons={!mediaPermissionsGranted}
            />

            {/* Waiting Room Overlay */}
            {isWaiting && (
              <MemberDisconnected
                mode="waiting"
                appointment={appointment}
                duration={duration}
                serviceDuration={appointment.serviceDuration}
                onMemberActionClick={handleMemberActionClick}
                mediaPermissionsGranted={mediaPermissionsGranted}
                memberMediaPermissionsGranted={memberMediaPermissionsGranted}
                twilioCallHandlers={{ callHandler, hangup, mute }}
                twilioCallState={callState}
                onGracePeriodExpired={onGracePeriodExpired}
              />
            )}
            <div
              className={cx({ [classes.subscriberVideoContainer]: participantScreenShared })}
              style={{
                display: subscriberJoined ? 'block' : 'none',
                height: memberVideoEnable ? '100%' : undefined,
                overflow: memberVideoEnable ? 'hidden' : undefined,
              }}
            >
              <div
                ref={subscriberContainerRef}
                className={cx({
                  [classes.subscriberVideoMini]: participantScreenShared,
                  [classes.subscriberVideo]: true,
                  [classes.streamHidden]: !memberVideoEnable,
                })}
              />
            </div>
            <div
              id="memberScreenContainer"
              className={participantScreenShared ? classes.screenShareVisible : classes.streamHidden}
            />
            {/* Avatar display when video is turned off but user is connected */}
            <Box
              className={classes.noVideo}
              style={{
                display:
                  subscriberJoined && !memberVideoEnable && !participantScreenShared ? 'block' : 'none',
                height:
                  subscriberJoined && !memberVideoEnable && !participantScreenShared ? '100%' : undefined,
                overflow:
                  subscriberJoined && !memberVideoEnable && !participantScreenShared ? 'hidden' : undefined,
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                flexDirection="column"
                height="100vh"
                justifyContent="center"
              >
                <Avatar
                  src={appointment?.participantImage}
                  name={appointment?.participantName}
                  size={128}
                  variant={avatarType.CIRCLE}
                />
                <Heading level={headingLevel.M} weight={fontWeight.BOLD} className={classes.username}>
                  {appointment?.participantName?.split(' ').slice(0, -1).join(' ')}
                </Heading>
                <Text className={classes.fullname} weight={fontWeight.SEMI_BOLD}>
                  {appointment?.participantName}
                </Text>
              </Box>
            </Box>
            {!subscriberJoined && !isWaiting && (
              <MemberDisconnected
                mode={callState === TWILIO_CALL_STATE.CONNECTED ? 'phone-connected' : 'disconnected'}
                firstName={appointment.patientFirstName}
                phoneNumber={appointment.patientPhoneNumber}
                duration={duration}
                serviceDuration={appointment.serviceDuration}
                onMemberActionClick={handleMemberActionClick}
                memberMediaPermissionsGranted={memberMediaPermissionsGranted}
                appointment={appointment}
                twilioCallHandlers={{ callHandler, hangup, mute }}
                twilioCallState={callState}
              />
            )}
            <BottomView
              hidden={isInPersonSessionCall}
              isSharing={showShareScreenSelection}
              isVideoCall={publishVideo}
              isAudio={publishAudio}
              onShareScreenClick={onShareScreenClick}
              onStopSharingClick={onStopSharingClick}
              toggleVideCall={toggleVideCall}
              toggleMic={toggleMic}
              disableScreenShare={isWaiting}
              showScratchpadButton={showScratchpadButton}
              onScratchpadClick={handleScratchpadClick}
            />

            {/* Scratchpad Overlay - appears above bottom view */}
            {scratchpadModalOpen && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 20,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '90%',
                  backgroundColor: colors.white,
                  borderRadius: '12px',
                  boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.16)',
                  zIndex: 10,
                }}
              >
                <Box padding="16px">
                  <Text level="M" weight={fontWeight.SEMI_BOLD}>
                    Add note
                  </Text>
                </Box>
                <Box className={classes.scratchpadInputBox}>
                  <Input
                    multiline
                    fullWidth
                    rows={3}
                    size={inputSize.M}
                    variant={inputType.DEFAULT}
                    placeholder="Enter your note"
                    value={scratchpadInput}
                    onChange={event => setScratchpadInput(event.target.value)}
                    className={classes.scratchpadInput}
                    onKeyDown={event => {
                      if (event.key === ' ') {
                        event.stopPropagation();
                      }
                    }}
                  />
                </Box>
                <Box display="flex" justifyContent="flex-end" gap={2} padding="16px">
                  <Button
                    size={btnSize.SMALL}
                    variant={btnType.TEXT}
                    className={classes.footerText}
                    onClick={handleScratchpadClose}
                  >
                    Cancel
                  </Button>
                  <Button size={btnSize.SMALL} variant={btnType.PRIMARY} onClick={handleScratchpadSave}>
                    Save note
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        );
      }}
    </TwilioCall>
  );
};

export { StreamContainer };
